/**
 * Past-jobs list helpers (TECH-JOB-SCREEN.md §3b P0) — pure, no JSX.
 *
 * The server now pages over a deterministic sort (scheduled_start DESC,
 * id DESC), but the client still de-dupes by job id when flattening pages
 * (belt & suspenders against overlapping fetches) and re-sorts before
 * deriving month groups, so headers are contiguous and every group is
 * internally ordered newest-first.
 */

import { orgDateKey } from "@/lib/datetime";

export interface PastJobLike {
    id: string;
    scheduled_start: string | null;
    updated_at?: string | null;
}

export interface MonthGroup<T extends PastJobLike> {
    key: string; // "2026-05"
    label: string; // "May 2026"
    jobs: T[];
}

function sortTs(job: PastJobLike): string {
    return job.scheduled_start || job.updated_at || "";
}

/** Flatten pages, de-dupe by id (first occurrence wins), sort (ts, id) desc. */
export function flattenPastPages<T extends PastJobLike>(pages: Array<{ jobs: T[] }>): T[] {
    const seen = new Set<string>();
    const flat: T[] = [];
    for (const page of pages) {
        for (const job of page.jobs) {
            if (seen.has(job.id)) continue;
            seen.add(job.id);
            flat.push(job);
        }
    }
    flat.sort((a, b) => {
        const aTs = sortTs(a);
        const bTs = sortTs(b);
        if (aTs !== bTs) return aTs < bTs ? 1 : -1;
        return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
    return flat;
}

/** Month groups derived AFTER the sort — contiguous, internally newest-first. */
export function groupByMonth<T extends PastJobLike>(jobs: T[]): MonthGroup<T>[] {
    const groups: MonthGroup<T>[] = [];
    let current: MonthGroup<T> | null = null;
    for (const job of jobs) {
        const ts = sortTs(job);
        if (!ts) continue;
        const d = new Date(ts);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!current || current.key !== key) {
            current = {
                key,
                label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
                jobs: [],
            };
            groups.push(current);
        }
        current.jobs.push(job);
    }
    return groups;
}

export interface DayGroup<T> {
    key: string; // "2026-09-30" or "unscheduled"
    label: string;
    jobs: T[];
}

/**
 * Upcoming jobs grouped by the org calendar day they're scheduled on, oldest
 * day first. #1692 — keyed with `orgDateKey`, not the UTC ISO date: a job at
 * 7 PM Central or later is already "tomorrow" in UTC and used to sit under the
 * next day's header.
 */
export function groupUpcomingByDay<T extends { scheduled_start: string | null }>(
    jobs: readonly T[],
    labelFor: (scheduledStart: string) => string,
): DayGroup<T>[] {
    const groups = new Map<string, { label: string; jobs: T[] }>();
    for (const j of jobs) {
        const d = j.scheduled_start ? new Date(j.scheduled_start) : null;
        const key = d ? orgDateKey(d) : "unscheduled";
        const label = d ? labelFor(d.toISOString()) : "Unscheduled";
        const bucket = groups.get(key);
        if (bucket) bucket.jobs.push(j);
        else groups.set(key, { label, jobs: [j] });
    }
    return Array.from(groups.entries())
        .sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0))
        .map(([key, v]) => ({ key, label: v.label, jobs: v.jobs }));
}
